import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const CartContext = createContext(null);

const getStoredCart = () => {
  try {
    const raw = localStorage.getItem('cart');
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(getStoredCart);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const canAddItem = useCallback(
    (item) => {
      const incomingRestaurantId = item.restaurant_id || item.restaurantId;
      const existingRestaurantId = cart[0]?.restaurant_id || cart[0]?.restaurantId;
      if (!existingRestaurantId || !incomingRestaurantId) return true;
      return existingRestaurantId === incomingRestaurantId;
    },
    [cart]
  );

  const addToCart = useCallback(
    (item) => {
      if (!canAddItem(item)) {
        return false;
      }
      setCart((prev) => {
        const existing = prev.find((p) => p.id === item.id);
        if (existing) {
          return prev.map((p) =>
            p.id === item.id ? { ...p, quantity: p.quantity + 1 } : p
          );
        }
        return [...prev, { ...item, quantity: 1 }];
      });
      return true;
    },
    [canAddItem]
  );

  const removeFromCart = useCallback((id) => {
    setCart((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const updateQuantity = useCallback((id, quantity) => {
    if (quantity <= 0) {
      removeFromCart(id);
      return;
    }
    setCart((prev) =>
      prev.map((item) => (item.id === id ? { ...item, quantity } : item))
    );
  }, [removeFromCart]);

  const clearCart = useCallback(() => setCart([]), []);

  const replaceCart = useCallback((items) => {
    setCart(Array.isArray(items) ? items : []);
  }, []);

  const getTotalPrice = useCallback(
    () => cart.reduce((sum, item) => sum + Number(item.price || 0) * item.quantity, 0),
    [cart]
  );

  const value = useMemo(
    () => ({
      cart,
      addToCart,
      canAddItem,
      removeFromCart,
      updateQuantity,
      clearCart,
      replaceCart,
      getTotalPrice,
    }),
    [cart, addToCart, canAddItem, clearCart, getTotalPrice, removeFromCart, replaceCart, updateQuantity]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
